import React, {Fragment, useCallback, useMemo} from 'react';
import {t} from '../../../../localization';
import {TeamMember} from '../../../../services';
import {
  Button,
  getCapitalized,
  getWeekdayNames,
  Layout,
  Ruler,
  Spacer,
  Text,
} from '@di-nxt/components';
import {AvailabilityDay} from './AvailabilityDay';
import {weekFormatter} from './functions';
import {MemberFieldEditor} from '../../Components';

type Props = {
  member: TeamMember;
  disabled: boolean;
  onEditModeChanged: (t: boolean) => void;
};

const days = [1, 2, 3, 4, 5, 6, 7];

export const AvailabilityEditor: React.FC<Props> = ({
  member,
  disabled,
  onEditModeChanged,
}) => {
  const weekdays = useMemo(
    () => getWeekdayNames().map(d => getCapitalized(d)),
    [],
  );

  const handleWeekdaySelected = useCallback(
    (
      week: number,
      day: number,
      updateState: React.Dispatch<
        React.SetStateAction<Array<Array<boolean>> | undefined>
      >,
    ) => {
      updateState(p => {
        if (p) {
          // Copy state
          const nextState = p.reduce(
            (weeks, days) =>
              weeks.concat([weekdays.map((_, idx) => days[idx])]),
            [] as Array<Array<boolean>>,
          );
          nextState[week][day - 1] = !nextState[week][day - 1];
          return nextState;
        }
        return p;
      });
    },
    [weekdays],
  );

  return (
    <MemberFieldEditor
      editKey="weeks"
      entity={member}
      title={t('screens.member.fields.availability.title')}
      description={t('screens.member.fields.availability.description')}
      onGetDisplayValue={(input?: Array<Array<boolean>>) =>
        input === undefined || input.length === 0
          ? t('screens.member.fields.availability.empty')
          : weekFormatter(input.length, weekdays, input)
      }
      disabled={disabled}
      onEditModeChanged={onEditModeChanged}>
      {(editState, setEditorState) => (
        <Layout
          paddingVertical={'14'}
          borderColor={'border_mono_15'}
          borderWidth="1"
          borderStyle="solid"
          vertical
          borderRadius="8">
          {new Array(editState?.length).fill(0).map((_, week) => (
            <Layout
              vertical
              paddingBottom={
                week === (editState?.length ?? 0) - 1 ? '8' : '16'
              }>
              <Layout paddingHorizontal="16" align="center" height={34}>
                <Layout flex={1}>
                  <Text variant="inline_14_emphasis">
                    {t('screens.member.fields.availability.week', {
                      week: week + 1,
                    })}
                  </Text>
                </Layout>
              </Layout>
              <Spacer height="8" />
              <Layout horizontal>
                {days.map(day => (
                  <Fragment key={day}>
                    <AvailabilityDay
                      selected={
                        (editState && editState[week][day - 1] === true) ??
                        false
                      }
                      title={weekdays[day - 1]}
                      value={day}
                      onSelected={d2 =>
                        handleWeekdaySelected(week, d2, setEditorState)
                      }
                    />
                    {day < 7 ? <Ruler vertical /> : null}
                  </Fragment>
                ))}
                {/** Remove week button */}
                <Layout paddingRight="16" paddingTop="8" width={34}>
                  {week > 0 ? (
                    <Button
                      kind="Clear"
                      size="Small"
                      leftIcon="close"
                      onPress={() =>
                        setEditorState(p => p?.filter((_, i) => i !== week))
                      }
                    />
                  ) : null}
                </Layout>
              </Layout>
            </Layout>
          ))}
          <Layout
            paddingHorizontal="16"
            paddingVertical="16"
            paddingTop="24"
            justify="flex-end">
            <Button
              kind="Secondary"
              size="Small"
              leftIcon="plus"
              title={t('screens.member.fields.availability.addweek')}
              onPress={() =>
                setEditorState(p => {
                  const retVal = p?.concat([weekdays.map(() => false)]);
                  return retVal;
                })
              }
            />
          </Layout>
        </Layout>
      )}
    </MemberFieldEditor>
  );
};
